import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatINR, fmtDate } from "@/lib/roles";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const downloadFile = (path, filename) => {
  const token = localStorage.getItem("pulb_token");
  fetch(`${API_BASE}${path}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => r.blob())
    .then((b) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(b);
      link.download = filename;
      link.click();
    });
};

export default function Reports() {
  const [collection, setCollection] = useState(null);
  const [defaulters, setDefaulters] = useState([]);
  const [outstanding, setOutstanding] = useState(null);
  const [wardWise, setWardWise] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/reports/collection"),
      api.get("/reports/top-defaulters?limit=10"),
      api.get("/reports/outstanding"),
      api.get("/reports/ward-wise"),
    ]).then(([a, b, c, d]) => {
      setCollection(a.data);
      setDefaulters(b.data.items || []);
      setOutstanding(c.data);
      setWardWise(d.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Analytics</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold">Reports</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadFile("/reports/collection.csv", "collection_report.csv")} data-testid="btn-export-csv">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" onClick={() => downloadFile("/reports/collection.xlsx", "collection_report.xlsx")} data-testid="btn-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button className="bg-[#0F2942]" onClick={() => downloadFile("/reports/collection.pdf", "collection_report.pdf")} data-testid="btn-export-pdf">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Total Collected</div>
          <div className="text-2xl font-heading font-bold tabular-nums text-[#0D7650]">{formatINR(collection.total)}</div>
          <div className="text-xs text-slate-500 mt-1">{collection.count} transactions</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Outstanding</div>
          <div className="text-2xl font-heading font-bold tabular-nums text-[#E8702A]">{formatINR(outstanding.total_outstanding)}</div>
          <div className="text-xs text-slate-500 mt-1">{outstanding.count} pending demands</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Wards Covered</div>
          <div className="text-2xl font-heading font-bold tabular-nums text-[#0F2942] dark:text-white">{wardWise.length}</div>
          <div className="text-xs text-slate-500 mt-1">Across municipal bodies</div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="defaulters" className="w-full">
        <TabsList>
          <TabsTrigger value="defaulters" data-testid="tab-defaulters">Top Defaulters</TabsTrigger>
          <TabsTrigger value="ward" data-testid="tab-ward">Ward-wise Collection</TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="tab-outstanding">Outstanding Demands</TabsTrigger>
        </TabsList>

        <TabsContent value="defaulters">
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Top 10 defaulters</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="defaulters-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left py-2 px-4">Property</th><th className="text-left py-2 px-4">Owner</th><th className="text-left py-2 px-4">Ward</th><th className="text-right py-2 px-4">Dues</th></tr>
                </thead>
                <tbody>
                  {defaulters.map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="py-2 px-4 font-mono text-xs">{p.property_id}</td>
                      <td className="py-2 px-4">{p.owner_name}</td>
                      <td className="py-2 px-4">{p.ward}</td>
                      <td className="py-2 px-4 text-right font-mono font-semibold text-red-600">{formatINR(p.total_dues)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ward">
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Ward-wise annual tax</CardTitle></CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={wardWise}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="ward" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => v > 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Bar dataKey="annual_tax" fill="#0F2942" radius={[4, 4, 0, 0]} name="Annual Tax" />
                  <Bar dataKey="total_dues" fill="#E8702A" radius={[4, 4, 0, 0]} name="Dues" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="outstanding">
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Outstanding demands</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="outstanding-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left py-2 px-4">Demand</th><th className="text-left py-2 px-4">Module</th><th className="text-left py-2 px-4">Period</th><th className="text-left py-2 px-4">Due</th><th className="text-right py-2 px-4">Outstanding</th></tr>
                </thead>
                <tbody>
                  {(outstanding.items || []).slice(0, 30).map((d) => (
                    <tr key={d.id} className="border-t">
                      <td className="py-2 px-4 font-mono text-xs">{d.demand_id}</td>
                      <td className="py-2 px-4 capitalize">{d.module.replace("_", " ")}</td>
                      <td className="py-2 px-4">{d.period}</td>
                      <td className="py-2 px-4">{fmtDate(d.due_date)}</td>
                      <td className="py-2 px-4 text-right font-mono font-semibold">{formatINR(d.total - (d.paid || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
